import { Injectable } from '@angular/core';
import { BehaviorSubject, map, Observable, of } from 'rxjs';
import {
	AbstractCRUDService,
	AlarisApiService,
	ExtendableRefBookService
} from '@campaign-portal/components-library';
import { HistoryLog } from '@helpers/types/models';
import { ReadRequest, ReadResponse } from '@campaign-portal/namespace/common/implementations';
import { Filter, RPCRequestParams } from '@campaign-portal/namespace/common/rpc.params';
import {
	HistoryLogsReadRequest,
	HistoryLogsReadResponse
} from '@helpers/types/hlr';

@Injectable({
	providedIn: 'root'
})
export class HistoryLogsService extends ExtendableRefBookService<HistoryLog> implements AbstractCRUDService {


	readonly entity = '';
	readonly title = '';
	override readonly loading$ = new BehaviorSubject<boolean>(false);
	readonly create = this.update;
	readonly delete = this.update;

	constructor(
		private readonly api: AlarisApiService
	) {
		super();
	}

	override process(resp: ReadResponse<HistoryLog<number | null>[]>): ReadResponse<HistoryLog<number | null>[]> {
		this.list$.next(resp.Data);
		this.map$.next(this.list.reduce((result, item) => {
			result.set(item.ID, item);
			return result;
		}, new Map()));
		return resp;
	}

	read(params?: ReadRequest): Observable<ReadResponse<HistoryLog[]>> {
		let parsedParams: HistoryLogsReadRequest = {
			active: 'id',
			direction: 'asc',
		}
		if (params) {
			parsedParams = this.prepareParams(params);
		}
		return this.api.loader<HistoryLogsReadResponse>('HistoryLogs.Read', parsedParams, this.loading$)
			.pipe(
				map((resp) => {
					return {
						Success: !!resp.data,
						Total: resp.length,
						Data: resp.data
					};
				}),
				map((resp) => {
					return this.process(resp);
				})
			);
	}

	loadDetails(log: HistoryLog): Observable<ReadResponse<HistoryLog[]>> {
		const params: HistoryLogsReadRequest = {
			active: 'id',
			direction: 'asc',
			actionID: log.ActionID
		}
		return this.api.loader<HistoryLogsReadResponse>('HistoryLogs.ReadDetailsByActionID',
			params, this.loading$)
			.pipe(
				map((resp) => {
					return {
						Success: !!resp.data,
						Total: resp.length,
						Data: resp.data
					};
				}),
				map((resp) => {
					return this.process(resp);
				})
			);
	}

	update(): Observable<void> {
		return of();
	}

	private prepareParams(params: RPCRequestParams): HistoryLogsReadRequest {
		return {
			offset: params.Paging?.Skip ?? 0,
			limit: params.Paging?.Take ?? 25,
			active: 'id',
			direction: 'asc',
			filter: this.parseFilter(params.Filters)
		};
	}

	private parseFilter(filters: Filter[] | undefined): { key: string, value: string }[] {
		const res: { key: string, value: string }[] = [];
		if (filters) {
			for (const filter of filters) {
				const variable = filter.Field;
				const value = filter.Value;
				switch (variable) {
					case 'ActionTime': {
						const start = (value as any).Start;
						const end = (value as any).End;
						if (start) {
							res.push({
								key: 'action_time_from',
								value: start
							})
						}
						if (end) {
							res.push({
								key: 'action_time_to',
								value: end
							})
						}
						break;
					}
					case 'RecordCount': {
						const start = (filter.Value as any).Start;
						const end = (filter.Value as any).End;
						if (start) {
							res.push({
								key: 'min_record_count',
								value: start + ''
							})
						}
						if (end) {
							res.push({
								key: 'max_record_count',
								value: end + ''
							})
						}
						break;
					}
					case 'ID':
						res.push({ key: 'id', value: value + '' });
						break;
					case 'ActionID':
						res.push({ key: 'action_id', value: value + '' });
						break;
					case 'TableName':
						res.push({ key: 'table_name', value: value + '' });
						break;
					case 'ActionType':
						res.push({ key: 'action_type', value: value + '' });
						break;
					case 'UserID':
						console.log(value)
						res.push({ key: 'user_id', value: (value as {name: string, value: number}).value + '' });
						break;
					default: {
						res.push({ key: variable, value: value + '' })
					}
				}
			}
		}
		return res;
	}

}
